import React, { useEffect, useState } from "react"
import { Modal, Button, Input, Flex, Divider } from "antd"
import { DeleteOutlined, EditOutlined, CheckOutlined, CloseOutlined, WarningFilled } from "@ant-design/icons"
import { useDispatch, useSelector } from "react-redux"
import PropTypes from "prop-types"
import { useMediaQuery } from "react-responsive"
import { createCategory, deleteCategory, getCategories, updateCategory } from "store/trips"
import { loadingSelector } from "modules/loader-watchers/selectors"
import CustomDrawer from "ui/Drawer"
import styles from "./index.module.css"

const ManageCategoriesModal = ({ visible, onClose }) => {
  const dispatch = useDispatch()
  const isMobile = useMediaQuery({ query: "(max-width: 767px)" })
  const categories = useSelector((state) => state.trips.categories.data?.categories || [])
  const isCategoryLoading = useSelector(loadingSelector("trips.categories"))

  const [newName, setNewName] = useState("")
  const [editingId, setEditingId] = useState(null)
  const [editingName, setEditingName] = useState("")
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [isAdding, setIsAdding] = useState(false)
  const [isEditSaving, setIsEditSaving] = useState(false)
  const [deletingId, setDeletingId] = useState(null)

  useEffect(() => {
    if (visible) {
      dispatch(getCategories())
      setNewName("")
      setEditingId(null)
      setEditingName("")
      setDeleteConfirm(null)
    }
  }, [visible, dispatch])

  const handleAdd = async () => {
    const name = newName.trim()
    if (!name) return
    setIsAdding(true)
    try {
      await dispatch(createCategory(name))
      setNewName("")
    } finally {
      setIsAdding(false)
    }
  }

  const startEdit = (category) => {
    setEditingId(category.id)
    setEditingName(category.name)
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditingName("")
  }

  const handleEditSave = async (id) => {
    const name = editingName.trim()
    if (!name) return
    setIsEditSaving(true)
    try {
      await dispatch(updateCategory(id, name))
      setEditingId(null)
      setEditingName("")
    } finally {
      setIsEditSaving(false)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return
    setDeletingId(deleteConfirm.id)
    try {
      await dispatch(deleteCategory(deleteConfirm.id))
      setDeleteConfirm(null)
    } finally {
      setDeletingId(null)
    }
  }

  const content = (
    <div className={styles.container}>
      <div className={styles.createSection}>
        <p className={styles.createLabel}>Create New Category</p>
        <Flex gap={12}>
          <Input
            placeholder="Enter category name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onPressEnter={handleAdd}
            className={styles.createInput}
            size="large"
            disabled={isAdding}
          />
          <Button
            size="large"
            color="primary"
            variant="solid"
            onClick={handleAdd}
            loading={isAdding}
            disabled={!newName.trim()}
          >
            Add
          </Button>
        </Flex>
      </div>

      <div className={styles.list}>
        {(() => {
          if (isCategoryLoading && categories.length === 0) return null
          if (categories.length === 0) return <p className={styles.emptyState}>No categories yet</p>
          return categories.map((category, index) => (
            <div key={category.id}>
              {index > 0 && <Divider className={styles.divider} />}
              {editingId === category.id ? (
                <Flex align="center" gap={10} className={styles.editRow}>
                  <Input
                    autoFocus
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onPressEnter={() => handleEditSave(category.id)}
                    className={styles.editInput}
                    size="large"
                    disabled={isEditSaving}
                  />
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={cancelEdit}
                    disabled={isEditSaving}
                    size="large"
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    variant="solid"
                    onClick={() => handleEditSave(category.id)}
                    loading={isEditSaving}
                    disabled={!editingName.trim()}
                    icon={<CheckOutlined />}
                    size="large"
                  >
                    Save
                  </Button>
                </Flex>
              ) : (
                <Flex align="center" justify="space-between" className={styles.categoryRow}>
                  <span className={styles.categoryTag}>{category.name}</span>
                  <Flex gap={8}>
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => startEdit(category)}
                      className={styles.actionBtn}
                    />
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() => setDeleteConfirm({ id: category.id, name: category.name })}
                      className={styles.actionBtn}
                    />
                  </Flex>
                </Flex>
              )}
            </div>
          ))
        })()}
      </div>
    </div>
  )

  const deleteModal = (
    <Modal
      open={!!deleteConfirm}
      onCancel={() => setDeleteConfirm(null)}
      footer={null}
      width={340}
      centered
      closeIcon={<CloseOutlined />}
      className={styles.deleteModal}
    >
      <div className={styles.deleteContent}>
        <WarningFilled className={styles.warningIcon} />
        <h3 className={styles.deleteTitle}>Delete Category?</h3>
        <p className={styles.deleteText}>
          Are you sure you want to delete category: <strong>{deleteConfirm?.name}</strong>?
        </p>
        <Flex gap={12} justify="center">
          <Button
            size="large"
            color="primary"
            variant="outlined"
            onClick={() => setDeleteConfirm(null)}
            disabled={!!deletingId}
          >
            Cancel
          </Button>
          <Button
            size="large"
            danger
            type="primary"
            onClick={handleDeleteConfirm}
            loading={!!deletingId}
          >
            Delete
          </Button>
        </Flex>
      </div>
    </Modal>
  )

  return (
    <>
      {isMobile ? (
        <CustomDrawer open={visible} setOpen={onClose} title="Manage Categories">
          {content}
        </CustomDrawer>
      ) : (
        <Modal
          open={visible}
          onCancel={onClose}
          footer={null}
          width={500}
          centered
          title={<span className={styles.modalTitle}>Manage Categories</span>}
          closeIcon={<CloseOutlined />}
          className={styles.modal}
          maskClosable={false}
        >
          {content}
        </Modal>
      )}
      {deleteModal}
    </>
  )
}

ManageCategoriesModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
}

export default ManageCategoriesModal
